import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api, onSessionExpired, refreshSession, setAccessToken } from './api';

export interface PendingMfaRole {
  roleKey: string;
  roleName: string;
  organizationId: string;
}

/** Respuesta de GET /api/auth/me. */
export interface Me {
  id: string;
  email: string;
  fullName: string;
  organizationId: string;
  tenantId: string;
  /** Su tenant es el operador de la plataforma: puede administrar otros tenants (si tiene el permiso). */
  platformOperator: boolean;
  organizationIds: string[];
  twoFactorEnabled: boolean;
  /** Permisos efectivos por organización: solo para mostrar u ocultar; el control real es de la API. */
  permissions: Record<string, string[]>;
  rolesPendingMfa: PendingMfaRole[];
}

type AuthState = { status: 'cargando' } | { status: 'anonimo'; expired?: boolean } | { status: 'autenticado'; me: Me };

interface TokenResponse {
  accessToken: string;
}

interface AuthContextValue {
  state: AuthState;
  /** Con 2FA activo devuelve el token del desafío; sin 2FA abre la sesión. */
  login(email: string, password: string): Promise<{ mfaToken?: string }>;
  verifyMfa(mfaToken: string, code: string): Promise<void>;
  logout(): Promise<void>;
  reloadMe(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'cargando' });

  const reloadMe = useCallback(async () => {
    const me = await api<Me>('/api/auth/me');
    setState({ status: 'autenticado', me });
  }, []);

  // Al abrir la aplicación: si la cookie de sesión sigue vigente, se recupera la sesión sin pedir login.
  useEffect(() => {
    onSessionExpired(() => {
      setAccessToken(null);
      setState({ status: 'anonimo', expired: true });
    });
    void (async () => {
      if (await refreshSession()) {
        try {
          await reloadMe();
          return;
        } catch {
          setAccessToken(null);
        }
      }
      setState({ status: 'anonimo' });
    })();
  }, [reloadMe]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await api<TokenResponse | { mfaRequired: true; mfaToken: string }>('/api/auth/login', { json: { email, password } });
      if ('mfaToken' in res) return { mfaToken: res.mfaToken };
      setAccessToken(res.accessToken);
      await reloadMe();
      return {};
    },
    [reloadMe],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, code: string) => {
      const res = await api<TokenResponse>('/api/auth/2fa/verify', { json: { mfaToken, code } });
      setAccessToken(res.accessToken);
      await reloadMe();
    },
    [reloadMe],
  );

  const logout = useCallback(async () => {
    try {
      await api('/api/auth/logout', { method: 'POST' });
    } finally {
      setAccessToken(null);
      setState({ status: 'anonimo' });
    }
  }, []);

  const value = useMemo(() => ({ state, login, verifyMfa, logout, reloadMe }), [state, login, verifyMfa, logout, reloadMe]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}

/** Usuario autenticado (solo dentro de rutas protegidas). */
export function useMe(): Me {
  const { state } = useAuth();
  if (state.status !== 'autenticado') throw new Error('useMe sin sesión');
  return state.me;
}
